import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments';
import { S3SignedUrl } from '../../models';

type Heic2AnyResult = Blob | Blob[];
type Heic2AnyFn = (options: {
  blob: Blob;
  toType: string;
  quality?: number;
}) => Promise<Heic2AnyResult>;

@Injectable({
  providedIn: 'root',
})
export class UploadS3Service {
  private httpClient = inject(HttpClient);
  private heic2anyPromise: Promise<Heic2AnyFn> | null = null;

  getSignedUrl(name: string, type: string): Observable<S3SignedUrl> {
    const params: HttpParams = new HttpParams().set('fileName', name).set('fileType', type);

    return this.httpClient.get<any>(`${environment.apiURL}/s3/sign`, { params });
  }

  uploadFile(signedUrl: string, file: File): Observable<any> {
    return this.httpClient.put<any>(signedUrl, file, {
      reportProgress: true,
      observe: 'events',
    });
  }

  getHeicToJpg(url: string): Observable<Blob> {
    return new Observable<Blob>((observer) => {
      this.httpClient.get(url, { responseType: 'blob' }).subscribe({
        next: async (blob: Blob) => {
          try {
            const heic2any = await this.getHeic2AnyConverter();
            const jpegBlob = await heic2any({ blob, toType: 'image/jpeg', quality: 0.7 });
            observer.next(this.normalizeHeicToJpegResult(jpegBlob));
            observer.complete();
          } catch (error) {
            observer.error(error);
          }
        },
        error: (error) => {
          observer.error(error);
        },
      });
    });
  }

  blobHeicToJpg(blob: Blob): Observable<Blob> {
    return new Observable<Blob>((observer) => {
      this.getHeic2AnyConverter()
        .then((heic2any) => heic2any({ blob, toType: 'image/jpeg', quality: 0.7 }))
        .then((jpegBlob) => {
          observer.next(this.normalizeHeicToJpegResult(jpegBlob));
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  private getHeic2AnyConverter(): Promise<Heic2AnyFn> {
    if (!this.heic2anyPromise) {
      this.heic2anyPromise = import('heic2any').then(
        (module) => module.default as unknown as Heic2AnyFn,
      );
    }

    return this.heic2anyPromise;
  }

  private normalizeHeicToJpegResult(result: Heic2AnyResult): Blob {
    return result instanceof Blob ? result : result[0];
  }
}
